import { type ComponentType, memo, useCallback, useMemo } from 'react';

import { useBreakpointsContext } from '@air/provider-media-query';

import {
  PrivateSearchCommandDragOverlay,
  type PrivateSearchCommandDropPayload,
} from '~/components/Gallery/PrivateSearchCommandDragOverlay';
import { PrivateBoardNullState } from '~/components/PrivateBoard/PrivateBoardNullState';
import { CustomFieldTableHeader } from '~/components/TableView/CustomFieldTableHeader/CustomFieldTableHeader';
import { type TableViewProps } from '~/components/TableView/index';
import { SelectableTableView, type SelectableTableViewProps } from '~/components/TableView/SelectableTableView';
import { TableHeaders } from '~/components/TableView/TableHeaders';
import { TablePageContainer } from '~/components/TableView/TablePageContainer';
import { TableViewProvider } from '~/components/TableView/TableViewContext';
import { type UsePrivateGetTableRowsParams, usePrivateTableRows } from '~/components/TableView/usePrivateTableRows';
import { useTableSelectableItems } from '~/components/TableView/useTableSelectableItems';
import { useTableHeaderPositionContext } from '~/components/v2/components/shared/table/TableHeaderPositionContextProvider';
import { BOARD_PAGE_TABLE_VIEW } from '~/constants/testIDs';
import { usePrivateSmartSearchSimilarToFilter } from '~/hooks/filters/usePrivateSmartSearchSimilarToFilter';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useFetchObjectsPermissions } from '~/hooks/useFetchObjectsPermissions';
import { useWorkspaceHorizontalPadding } from '~/hooks/useWorkspaceHorizontalPadding';
import { usePrivateSearchContext } from '~/providers/PrivateSearchProvider';
import { usePrivateTableViewItems } from '~/swr-hooks/gallery/tableView/usePrivateTableViewItems';
import { itemIsAsset, itemIsBoard, itemIsFile } from '~/swr-hooks/gallery/types';
import { TableViewItemRenamingProvider } from './PrivateTableView/TableViewItemRenamingContext';

export type PrivateTableViewProps = {
  EmptyState?: ComponentType;
  scrollElementRef: SelectableTableViewProps['scrollElementRef'];
  showBoards?: boolean;
  showUploads?: boolean;
} & Pick<UsePrivateGetTableRowsParams, 'renderAssetRow' | 'renderBoardRow'>;

export const PrivateTableView = memo(
  ({
    EmptyState = PrivateBoardNullState,
    scrollElementRef,
    renderAssetRow,
    renderBoardRow,
    showBoards = true,
    showUploads = true,
  }: PrivateTableViewProps) => {
    const { isAboveMediumScreen } = useBreakpointsContext();
    const { responsiveHorizontalPadding } = useWorkspaceHorizontalPadding();
    const { crossLocationSelectionEnabled } = useFeatureFlags();
    const { tableHeaderTopPosition } = useTableHeaderPositionContext();
    const { addSimilarToFilter } = usePrivateSmartSearchSimilarToFilter();
    const { isSemanticSearchEnabled, isHybridSemanticSearchEnabled } = usePrivateSearchContext();

    const isBoardSelectable = useCallback(() => true, []);
    const isAssetSelectable = useCallback(() => true, []);

    const { data, uploads, hasMore, isEmpty, isLoading, isLoadingMore, loadNextPage } = usePrivateTableViewItems({
      showBoards,
      showUploads,
      semantic: isSemanticSearchEnabled,
      hybrid: isHybridSemanticSearchEnabled,
    });

    const assets = useMemo(() => data?.filter(itemIsAsset).map((item) => item.item), [data]);
    const files = useMemo(() => data?.filter(itemIsFile).map((item) => item.item), [data]);
    const boards = useMemo(() => data?.filter(itemIsBoard).map((item) => item.item), [data]);

    useFetchObjectsPermissions({
      objects: {
        boardIds: boards.map(({ id }) => id),
        clipIds: [...assets, ...files].map(({ id }) => id),
        /**
         * We need to fetch permissions for assets because we use their permissions for certain endpoints
         * @see https://air-labs-team.slack.com/archives/C052RDYCGAG/p1695838522133639
         */
        assetIds: [...assets, ...files].map(({ assetId }) => assetId),
      },
    });

    const rows = usePrivateTableRows({
      data,
      hasMore,
      isLoading,
      isLoadingMore,
      uploads,
      loadMore: loadNextPage,
      renderAssetRow,
      renderBoardRow,
      isAssetSelectable,
      isBoardSelectable,
    });

    const selectableItems = useMemo(
      () =>
        (data || []).filter((item) => {
          if (itemIsBoard(item)) {
            return isBoardSelectable();
          } else {
            return isAssetSelectable();
          }
        }),
      [data, isAssetSelectable, isBoardSelectable],
    );

    const hasData = !!data?.length;

    const renderTableHeaders: Required<TableViewProps>['renderHeaders'] = useCallback(
      (ref) =>
        (hasData || isLoading || isLoadingMore) &&
        isAboveMediumScreen && (
          <TableHeaders
            ref={ref}
            CustomFieldTableHeaderComponent={CustomFieldTableHeader}
            horizontalPadding={responsiveHorizontalPadding}
            topPos={tableHeaderTopPosition}
          />
        ),
      [hasData, isLoading, isLoadingMore, isAboveMediumScreen, responsiveHorizontalPadding, tableHeaderTopPosition],
    );

    useTableSelectableItems(selectableItems ?? [], crossLocationSelectionEnabled);

    const onAssetDroppedOnSearchCommand = useCallback(
      ({ item }: PrivateSearchCommandDropPayload) => {
        addSimilarToFilter(item);
      },
      [addSimilarToFilter],
    );

    return (
      <TableViewProvider>
        <TableViewItemRenamingProvider>
          <TablePageContainer data-testid={BOARD_PAGE_TABLE_VIEW}>
            <PrivateSearchCommandDragOverlay onDrop={onAssetDroppedOnSearchCommand} />
            {isEmpty ? (
              <EmptyState />
            ) : (
              <SelectableTableView
                canScrollHorizontally={isAboveMediumScreen && hasData}
                horizontalPadding={responsiveHorizontalPadding}
                renderHeaders={renderTableHeaders}
                rows={rows}
                scrollElementRef={scrollElementRef}
              />
            )}
          </TablePageContainer>
        </TableViewItemRenamingProvider>
      </TableViewProvider>
    );
  },
);

PrivateTableView.displayName = 'PrivateTableView';
